import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import AlumniDropdown from "../components/AlumniDropdown";
import "./GenericProgram.css";
import { getAllPrograms } from "./programIndex";
import phdBrochureFinalDocument from "../assets/documents/PhD Brochure_Final.pdf";
import phdProposalsJuly2026Document from "../assets/documents/PhD_Proposals_July_2026_document _Final.pdf";

const RESOURCE_LABELS = {
  brochure: "Brochure",
  curriculum: "Curriculum",
  timeTable: "Time Table",
  admissionPortal: "Admission Portal",
  website: "Program Website",
  cipWebsite: "CIP Website",
  pgAcademics: "Postgraduate Academics",
  mtechAdmissions: "M.Tech Admissions",
};

const ADMISSION_ASSET_LINKS = {
  phdBrochureFinalDocument,
  phdProposalsJuly2026Document,
};

function findProgram(programId) {
  const allPrograms = getAllPrograms();
  return allPrograms.find((program) => program.id === programId) || null;
}

function hasContent(value) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value && typeof value === "object") {
    return Object.values(value).some((item) => hasContent(item));
  }

  return value !== undefined && value !== null && String(value).trim() !== "";
}

function formatLabel(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function resolveAdmissionLinkHref(link) {
  if (!link) {
    return "";
  }

  if (typeof link === "string") {
    return link;
  }

  if (link.assetKey && ADMISSION_ASSET_LINKS[link.assetKey]) {
    return ADMISSION_ASSET_LINKS[link.assetKey];
  }

  return link.href || "";
}

function loadProgramDetails(programMeta) {
  if (!programMeta) {
    return null;
  }

  try {
    let dataset = null;

    if (programMeta.category === "M.Tech") {
      dataset = require("../programDetails/mtechPrograms.json");
    } else if (programMeta.category === "Online M.Tech") {
      dataset = require("../programDetails/onlineMtechPrograms.json");
    } else if (programMeta.category === "M.Sc") {
      dataset = require("../programDetails/mscPrograms.json");
    } else if (programMeta.category === "PhD") {
      dataset = require("../programDetails/phdPrograms.json");
    }

    if (!dataset) {
      return null;
    }

    return (
      dataset[programMeta.id] ||
      Object.values(dataset).find((program) => program.id === programMeta.id) ||
      null
    );
  } catch (error) {
    return null;
  }
}

const SectionHeading = ({ children }) => (
  <h2 className="generic-section-heading">{children}</h2>
);

const BulletList = ({ items }) => (
  <ul className="generic-list">
    {(items || []).map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const InfoCard = ({ label, value }) => (
  <div className="generic-info-card">
    <span className="generic-info-label">{label}</span>
    <div className="generic-info-value">{value}</div>
  </div>
);

const PersonCard = ({ person }) => (
  <article className="generic-entity-card">
    <h3 className="generic-card-title">{person.name || "Details unavailable"}</h3>
    {person.role && <p className="generic-card-meta">{person.role}</p>}
    {person.department && <p className="generic-card-meta">{person.department}</p>}
    {person.expertise && <p className="generic-card-text">{person.expertise}</p>}
    {person.rollNo && <p className="generic-card-meta">{person.rollNo}</p>}
    {person.email && (
      <a className="generic-link" href={`mailto:${person.email}`}>
        {person.email}
      </a>
    )}
    {person.phone && <p className="generic-card-text">{person.phone}</p>}
  </article>
);

const RichField = ({ label, value }) => {
  if (!hasContent(value)) {
    return null;
  }

  return (
    <section className="generic-section-block">
      <SectionHeading>{label}</SectionHeading>
      {Array.isArray(value) ? (
        <BulletList items={value} />
      ) : value && typeof value === "object" ? (
        <div className="generic-info-grid">
          {Object.entries(value)
            .filter(([, nestedValue]) => hasContent(nestedValue))
            .map(([nestedLabel, nestedValue]) => (
              <InfoCard
                key={nestedLabel}
                label={formatLabel(nestedLabel)}
                value={nestedValue}
              />
            ))}
        </div>
      ) : (
        <p className="generic-text">{value}</p>
      )}
    </section>
  );
};

const GenericProgram = () => {
  const { programId, section } = useParams();
  const sectionKey = (section || "about").toLowerCase();

  const [lightboxSrc, setLightboxSrc] = useState(null);

  const programMeta = findProgram(programId);
  const program = loadProgramDetails(programMeta);

  if (!program || !programMeta) {
    return (
      <div className="generic-program-content generic-empty-state">
        <h1 className="program-page-title">Program not found</h1>
        <p className="generic-text">
          Unable to locate details for the requested program.
        </p>
      </div>
    );
  }

  const aboutData = program.about || {};
  const peopleData = program.people || {};
  const admissionsData = program.admissions || {};
  const researchData = program.research || {};
  const curriculumData = program.curriculum || {};
  const resourcesData = program.resources || {};
  const contactData = program.contact || {};

  const curriculumEntries = Object.entries(curriculumData).filter(([, value]) =>
    hasContent(value)
  );
  const resourceEntries = Object.entries(resourcesData).filter(([, value]) =>
    hasContent(value)
  );

  return (
    <article className="generic-program-content">
      <header className="generic-program-header">
        <span className="generic-program-badge">{programMeta.category}</span>
        <h1 className="program-page-title">{program.name}</h1>
        <div className="program-title-underline" />
      </header>

      {sectionKey === "about" && (
        <div className={aboutData.brochureImage ? "flex flex-col md:flex-row gap-8" : ""}>
          <div className="generic-section-stack flex-1">
            {aboutData.description && (
              <p className="generic-lead">{aboutData.description}</p>
            )}

            <div className="generic-info-grid">
              {aboutData.duration && (
                <InfoCard label="Duration" value={aboutData.duration} />
              )}
              {aboutData.intake && (
                <InfoCard label="Intake" value={aboutData.intake} />
              )}
            </div>

            <RichField label="Program Highlights" value={aboutData.highlights} />
            <RichField label="Program Structure" value={aboutData.programStructure} />

            {hasContent(aboutData.objectives) && (
              <section className="generic-section-block">
                <SectionHeading>Program Objectives</SectionHeading>
                <BulletList items={aboutData.objectives} />
              </section>
            )}

            <RichField label="Technology Domains" value={aboutData.technologyDomains} />

            {hasContent(aboutData.researchVerticals) && (
              <section className="generic-section-block">
                <SectionHeading>Research Verticals</SectionHeading>
                <BulletList items={aboutData.researchVerticals} />
              </section>
            )}
          </div>
          
          {/* Brochure image */}
          {aboutData.brochureImage && (() => {
            const imgSrc = require(`../assets/images/${aboutData.brochureImage}`);
            return (
              <div className="flex-shrink-0 md:w-80 w-full mt-6 md:mt-0">
                <img
                  src={imgSrc}
                  alt="Program Brochure"
                  className="w-full rounded-lg shadow-md object-cover border border-gray-200 cursor-pointer transition-transform duration-200 hover:scale-[1.02]"
                  style={{ maxHeight: "500px", objectPosition: "top" }}
                  onClick={() => setLightboxSrc(imgSrc)}
                  title="Click to enlarge"
                />
                <p className="text-center text-xs text-gray-500 mt-1">Program Brochure &mdash; click to enlarge</p>
              </div>
            );
          })()}
        </div>
      )}

      {sectionKey === "people" && (
        <div className="generic-section-stack">
          {Array.isArray(peopleData.coordinators) &&
            peopleData.coordinators.length > 0 && (
              <section className="generic-section-block">
                <SectionHeading>Coordinators</SectionHeading>
                <div className="generic-entity-grid">
                  {peopleData.coordinators.map((person, index) => (
                    <PersonCard key={index} person={person} />
                  ))}
                </div>
              </section>
            )}

          {Array.isArray(peopleData.faculty) && peopleData.faculty.length > 0 && (
            <section className="generic-section-block">
              <SectionHeading>Faculty</SectionHeading>
              <div className="generic-entity-grid">
                {peopleData.faculty.map((person, index) => (
                  <PersonCard key={index} person={person} />
                ))}
              </div>
            </section>
          )}

          {typeof peopleData.faculty === "string" && (
            <section className="generic-section-block">
              <SectionHeading>Faculty</SectionHeading>
              <p className="generic-text">{peopleData.faculty}</p>
            </section>
          )}

          {Array.isArray(peopleData.students) && peopleData.students.length > 0 && (
            <section className="generic-section-block">
              <AlumniDropdown title="Students" people={peopleData.students} />
            </section>
          )}

          <AlumniDropdown title="Alumni" people={peopleData.alumni} />
        </div>
      )}

      {sectionKey === "admissions" && (
        <div className="generic-section-stack">
          {admissionsData.mode && (
            <div className="generic-info-grid">
              <InfoCard label="Mode" value={admissionsData.mode} />
            </div>
          )}

          <RichField label="Eligibility" value={admissionsData.eligibility} />
          <RichField label="Fees" value={admissionsData.fees} />

          {hasContent(admissionsData.importantDates) && (
            <section className="generic-section-block">
              <SectionHeading>Important Dates</SectionHeading>
              <div className="generic-info-grid">
                {Object.entries(admissionsData.importantDates)
                  .filter(([, value]) => hasContent(value))
                  .map(([label, value]) => (
                    <InfoCard
                      key={label}
                      label={formatLabel(label)}
                      value={value}
                    />
                  ))}
              </div>
            </section>
          )}

          {Array.isArray(admissionsData.admissionLinks) &&
            admissionsData.admissionLinks.length > 0 && (
              <section className="generic-section-block">
                <SectionHeading>Admission Links</SectionHeading>
                <div className="generic-resource-grid generic-admission-links-grid">
                  {admissionsData.admissionLinks
                    .map((link, index) => {
                      const href = resolveAdmissionLinkHref(link);
                      if (!href) {
                        return null;
                      }

                      return (
                        <a
                          key={link.label || href || index}
                          className="generic-resource-card generic-admission-link-card"
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <span className="generic-admission-link-kicker">
                            {link.kicker || `${programMeta.category} Admissions`}
                          </span>
                          <span className="generic-card-title">
                            {link.label || `Admission Link ${index + 1}`}
                          </span>
                          <span className="generic-resource-meta">
                            {link.description || "Open resource"}
                          </span>
                          <span className="generic-admission-link-arrow" aria-hidden="true">
                            <FaArrowRight />
                          </span>
                        </a>
                      );
                    })
                    .filter(Boolean)}
                </div>
              </section>
            )}

          <RichField
            label="Selection Process"
            value={admissionsData.selectionProcess}
          />
        </div>
      )}

      {sectionKey === "research" && (
        <div className="generic-section-stack">
          <RichField label="Research Areas" value={researchData.areas} />
          <RichField label="Facilities" value={researchData.facilities} />
          <RichField
            label="Key Highlights"
            value={program.researchHighlights && program.researchHighlights.keyHighlights}
          />
          <RichField
            label="Student Achievements"
            value={
              program.researchHighlights &&
              program.researchHighlights.studentAchievements
            }
          />
          <RichField
            label="Key Publications"
            value={
              program.researchHighlights &&
              program.researchHighlights.keyPublications
            }
          />
          <RichField
            label="Collaborations"
            value={program.researchHighlights && program.researchHighlights.collaborations}
          />
          <RichField label="Career Prospects" value={program.careerProspects} />
        </div>
      )}

      {sectionKey === "resources" && (
        <div className="generic-section-stack">
          {curriculumEntries.length > 0 && (
            <section className="generic-section-block">
              <SectionHeading>Curriculum</SectionHeading>
              <div className="generic-curriculum-grid">
                {curriculumEntries.map(([label, value]) => (
                  <div key={label} className="generic-curriculum-card">
                    <h3 className="generic-card-title">{formatLabel(label)}</h3>
                    {Array.isArray(value) ? (
                      <BulletList items={value} />
                    ) : (
                      <p className="generic-text">{value}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {resourceEntries.length > 0 && (
            <section className="generic-section-block">
              <SectionHeading>Resources</SectionHeading>
              <div className="generic-resource-grid">
                {resourceEntries.map(([label, value]) => {
                  const href = String(value);
                  const external = href.startsWith("http");

                  return (
                    <a
                      key={label}
                      className="generic-resource-card"
                      href={href}
                      target={external ? "_blank" : undefined}
                      rel={external ? "noreferrer" : undefined}
                    >
                      <span className="generic-card-title">
                        {RESOURCE_LABELS[label] || formatLabel(label)}
                      </span>
                      <span className="generic-resource-meta">
                        {external ? "Open external resource" : "Open resource"}
                      </span>
                    </a>
                  );
                })}
              </div>
            </section>
          )}

        </div>
      )}

      {sectionKey === "contact" && (
        <div className="generic-section-stack">
          <section className="generic-section-block">
            <SectionHeading>Contact Information</SectionHeading>
            <div className="generic-info-grid">
              {contactData.email && (
                <InfoCard
                  label="Email"
                  value={
                    <a className="generic-link" href={`mailto:${contactData.email}`}>
                      {contactData.email}
                    </a>
                  }
                />
              )}
              {contactData.phone && (
                <InfoCard label="Phone" value={contactData.phone} />
              )}
              {contactData.location && (
                <InfoCard label="Location" value={contactData.location} />
              )}
            </div>
          </section>

          {hasContent(contactData.placementCoordinator) && (
            <section className="generic-section-block">
              <SectionHeading>Placement Coordinator</SectionHeading>
              <div className="generic-entity-grid">
                <PersonCard person={contactData.placementCoordinator} />
              </div>
            </section>
          )}
        </div>
      )}

      {sectionKey !== "about" &&
        sectionKey !== "people" &&
        sectionKey !== "admissions" &&
        sectionKey !== "research" &&
        sectionKey !== "resources" &&
        sectionKey !== "contact" && (
          <p className="generic-text">No information available for this section.</p>
        )}

      {sectionKey === "resources" &&
        curriculumEntries.length === 0 &&
        resourceEntries.length === 0 && (
          <p className="generic-text">No information available for this section.</p>
        )}

      {/* Lightbox modal */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
          onClick={() => setLightboxSrc(null)}
        >
          <div
            className="relative max-w-3xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightboxSrc(null)}
              className="absolute -top-10 right-0 text-white text-3xl font-bold leading-none hover:text-gray-300"
              aria-label="Close"
            >
              &times;
            </button>
            <img
              src={lightboxSrc}
              alt="Program Brochure (enlarged)"
              className="w-full rounded-lg shadow-2xl object-contain bg-white"
              style={{ maxHeight: "80vh" }}
            />
          </div>
        </div>
      )}
    </article>
  );
};

export default GenericProgram;
